import API, { type QueryParams } from '@module/API';
import Helper from '@module/Helper';
import type {
    ArchiveFilterModule,
    ArchiveFiltersLoadingChangeCallback,
    ArchiveFiltersState,
    ArchiveFiltersStateChangeCallback,
} from './types';

type ResettableFilterKey = Exclude<keyof ArchiveFiltersState, 'page'>;

class ArchiveFiltersController {
    private client: API;
    private container: HTMLElement | null = null;
    private queryElement: HTMLElement | null = null;
    private baseParams: QueryParams = {};
    private state: ArchiveFiltersState = {
        search: '',
        post_tag: 'all',
        school: 'all',
        programCat: 'all',
        programDirection: 'all',
        programActivity: 'all',
        page: 1,
    };
    private isLoading = false;
    private stateChangeCallbacks: ArchiveFiltersStateChangeCallback[] = [];
    private loadingChangeCallbacks: ArchiveFiltersLoadingChangeCallback[] = [];
    private modules: ArchiveFilterModule[] = [];

    constructor(client: API) {
        this.client = client;
        this.init();
    }

    public register(module: ArchiveFilterModule): this {
        this.modules.push(module);

        return this;
    }

    public registerMany(modules: ArchiveFilterModule[]): this {
        modules.forEach((module) => {
            this.register(module);
        });

        return this;
    }

    public boot(): this {
        this.modules.forEach((module) => {
            module.mount(this);
        });

        return this;
    }

    private init(): void {
        this.container = document.querySelector('[data-container]');
        this.queryElement = document.querySelector('[data-query]');

        if (!this.container || !this.queryElement) {
            return;
        }

        this.parseBaseParams();
        this.initializeStateFromUrl();
    }

    private parseBaseParams(): void {
        if (!this.queryElement) {
            return;
        }

        try {
            const queryData = this.queryElement.dataset.query;

            if (queryData) {
                this.baseParams = JSON.parse(queryData);
                this.normalizeBaseParams();
            }
        } catch (error) {
            console.error('Failed to parse query parameters:', error);
            this.baseParams = {};
        }
    }

    private normalizeBaseParams(): void {
        const rawBaseParams = this.baseParams as QueryParams & Record<string, unknown>;

        delete this.baseParams.s;
        delete this.baseParams.paged;
        delete rawBaseParams.post_tag;
        delete rawBaseParams.program_cat;
        delete rawBaseParams.program_direction;
        delete rawBaseParams.program_activity;
        delete rawBaseParams._school;
        delete rawBaseParams.uu_skip_hide_past_programs;

        if (Array.isArray(this.baseParams.tax_query)) {
            this.baseParams.tax_query = this.baseParams.tax_query.filter((item) => {
                if (!item || typeof item !== 'object') {
                    return true;
                }

                return item.taxonomy !== 'post_tag'
                    && item.taxonomy !== 'program_cat'
                    && item.taxonomy !== 'program_direction';
            });

            if (!this.baseParams.tax_query.length) {
                delete this.baseParams.tax_query;
            }
        }

        if (Array.isArray(this.baseParams.meta_query)) {
            this.baseParams.meta_query = this.baseParams.meta_query.filter((item) => {
                if (!item || typeof item !== 'object') {
                    return true;
                }

                return item.key !== 'school';
            });

            if (!this.baseParams.meta_query.length) {
                delete this.baseParams.meta_query;
            }
        }
    }

    private initializeStateFromUrl(): void {
        const url = new URL(window.location.href);

        const searchParam = url.searchParams.get('search');

        if (searchParam) {
            this.state.search = searchParam;
        }

        if (this.usesSchoolFilter()) {
            const schoolParam = url.searchParams.get('_school');

            if (schoolParam) {
                this.state.school = schoolParam;
            }
        } else {
            const postTagParam = url.searchParams.get('post_tag');

            if (postTagParam) {
                this.state.post_tag = postTagParam;
            }
        }

        const programCatParam = url.searchParams.get('program_cat');

        if (programCatParam) {
            this.state.programCat = programCatParam;
        }

        const programDirectionParam = url.searchParams.get('program_direction');

        if (programDirectionParam) {
            this.state.programDirection = programDirectionParam;
        }

        const programActivityParam = url.searchParams.get('program_activity');

        if (programActivityParam) {
            this.state.programActivity = programActivityParam;
        }

        const pageMatch = url.pathname.match(/\/page\/(\d+)/);

        if (pageMatch) {
            this.state.page = parseInt(pageMatch[1], 10);
        }
    }

    public onStateChange(callback: ArchiveFiltersStateChangeCallback): void {
        this.stateChangeCallbacks.push(callback);
    }

    public onLoadingChange(callback: ArchiveFiltersLoadingChangeCallback): void {
        this.loadingChangeCallbacks.push(callback);
    }

    private notifyStateChange(): void {
        this.stateChangeCallbacks.forEach((callback) => {
            callback({ ...this.state });
        });
    }

    private notifyLoadingChange(): void {
        this.loadingChangeCallbacks.forEach((callback) => {
            callback(this.isLoading);
        });
    }

    private updateStateValue<K extends ResettableFilterKey>(key: K, value: ArchiveFiltersState[K]): void {
        if (this.state[key] === value) {
            return;
        }

        this.state[key] = value;
        this.state.page = 1;
        this.fetchAndUpdate();
    }

    public setSearch(query: string): void {
        this.updateStateValue('search', query);
    }

    public setPostTag(tagId: string | null): void {
        this.updateStateValue('post_tag', tagId);
    }

    public setSchool(schoolId: string | null): void {
        this.updateStateValue('school', schoolId);
    }

    public setProgramCat(programCat: string | null): void {
        this.updateStateValue('programCat', programCat);
    }

    public setProgramDirection(programDirection: string | null): void {
        this.updateStateValue('programDirection', programDirection);
    }

    public setProgramActivity(programActivity: string | null): void {
        this.updateStateValue('programActivity', programActivity);
    }

    public setPage(page: number): void {
        if (this.state.page === page) {
            return;
        }

        this.state.page = page;
        this.fetchAndUpdate();
    }

    public getState(): ArchiveFiltersState {
        return { ...this.state };
    }

    public getIsLoading(): boolean {
        return this.isLoading;
    }

    public usesSchoolFilter(): boolean {
        return ['program', 'person'].includes(String(this.baseParams.post_type ?? ''));
    }

    public getActiveTagFilter(): string | null {
        return this.usesSchoolFilter() ? this.state.school : this.state.post_tag;
    }

    private getBaseTaxQuery(): NonNullable<QueryParams['tax_query']> {
        return Array.isArray(this.baseParams.tax_query) ? [...this.baseParams.tax_query] : [];
    }

    private getBaseMetaQuery(): NonNullable<QueryParams['meta_query']> {
        return Array.isArray(this.baseParams.meta_query) ? [...this.baseParams.meta_query] : [];
    }

    private getSchoolIdBySlug(slug: string): number | null {
        const schoolButton = document.querySelector<HTMLButtonElement>(`button[data-type="school"][data-tag="${slug}"]`);

        if (!schoolButton) {
            return null;
        }

        const schoolId = Number(schoolButton.dataset.schoolId ?? '');

        return Number.isFinite(schoolId) && schoolId > 0 ? schoolId : null;
    }

    private buildQueryParams(): QueryParams {
        const params: QueryParams = {
            ...this.baseParams,
            paged: this.state.page,
        };

        if (this.state.search) {
            params.s = this.state.search;
        }

        const activeTag = this.getActiveTagFilter();
        const taxQuery = this.getBaseTaxQuery();
        const metaQuery = this.getBaseMetaQuery();

        if (activeTag && activeTag !== 'all') {
            if (this.usesSchoolFilter()) {
                const schoolId = this.getSchoolIdBySlug(activeTag);

                if (schoolId) {
                    metaQuery.push({
                        key: 'school',
                        value: schoolId,
                        compare: '=',
                    });
                }
            } else {
                taxQuery.push({
                    taxonomy: 'post_tag',
                    field: 'slug',
                    terms: activeTag,
                });
            }
        }

        if (this.state.programCat && this.state.programCat !== 'all') {
            taxQuery.push({
                taxonomy: 'program_cat',
                field: 'slug',
                terms: this.state.programCat,
            });
        }

        if (this.state.programDirection && this.state.programDirection !== 'all') {
            taxQuery.push({
                taxonomy: 'program_direction',
                field: 'slug',
                terms: this.state.programDirection,
            });
        }

        if (this.state.programActivity && this.state.programActivity !== 'all') {
            params.program_activity = this.state.programActivity;
        }

        if (taxQuery.length) {
            params.tax_query = taxQuery;
        }

        if (metaQuery.length) {
            params.meta_query = metaQuery;
        }

        return params;
    }

    private async fetchAndUpdate(): Promise<void> {
        if (!this.container) {
            return;
        }

        const params = this.buildQueryParams();

        try {
            this.setLoadingState(true);
            const response = await this.client.getPosts(params);

            this.updateResults(response);
            this.updateBrowserUrl();
            this.notifyStateChange();
        } catch (error) {
            console.error('Posts fetch error:', error);
            this.showError();
        } finally {
            this.setLoadingState(false);
        }
    }

    private updateResults(response: string): void {
        if (!this.container) {
            return;
        }

        if (response) {
            this.container.innerHTML = response;
        }
    }

    private updateBrowserUrl(): void {
        try {
            const url = new URL(window.location.href);

            url.pathname = url.pathname.replace(/\/page\/\d+/, '');

            if (this.state.search) {
                url.searchParams.set('search', this.state.search);
            } else {
                url.searchParams.delete('search');
            }

            const activeTag = this.getActiveTagFilter();

            if (activeTag && activeTag !== 'all') {
                if (this.usesSchoolFilter()) {
                    url.searchParams.set('_school', activeTag);
                    url.searchParams.delete('post_tag');
                } else {
                    url.searchParams.set('post_tag', activeTag);
                    url.searchParams.delete('_school');
                }
            } else {
                url.searchParams.delete('post_tag');
                url.searchParams.delete('_school');
            }

            if (this.state.programCat && this.state.programCat !== 'all') {
                url.searchParams.set('program_cat', this.state.programCat);
            } else {
                url.searchParams.delete('program_cat');
            }

            if (this.state.programDirection && this.state.programDirection !== 'all') {
                url.searchParams.set('program_direction', this.state.programDirection);
            } else {
                url.searchParams.delete('program_direction');
            }

            if (this.state.programActivity && this.state.programActivity !== 'all') {
                url.searchParams.set('program_activity', this.state.programActivity);
            } else {
                url.searchParams.delete('program_activity');
            }

            if (this.state.page > 1) {
                url.pathname = url.pathname.replace(/\/$/, '') + `/page/${this.state.page}`;
            }

            window.history.pushState({}, '', url.pathname + url.search);

            if (url.searchParams.has('_school')) {
                document.querySelector('[data-query]')?.setAttribute('data-school', url.searchParams.get('_school') || '');
            } else {
                document.querySelector('[data-query]')?.removeAttribute('data-school');
            }
        } catch (error) {
            console.error('Failed to update browser URL:', error);
        }
    }

    private setLoadingState(isLoading: boolean): void {
        if (this.isLoading === isLoading) {
            return;
        }

        this.isLoading = isLoading;

        if (isLoading && this.container) {
            Helper.printLoadingGrid(this.container);
        }

        this.notifyLoadingChange();
    }

    private showError(): void {
        if (!this.container) {
            return;
        }

        this.container.innerHTML = `
            <div class="p-8 text-center text-red-600">
                <p>Виникла помилка при завантаженні. Будь ласка, спробуйте ще раз.</p>
            </div>
        `;
    }

    public refresh(): void {
        this.fetchAndUpdate();
    }
}

export default ArchiveFiltersController;